import * as React from 'react';
import type { AskBlock } from '@/types/api';
import { ApiError } from '@/lib/api';
import { errorMessage } from '@/lib/copy';
import { formatRemaining } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { useAnswerAsk } from './queries';

/* ---------------------------------------------------------------------------
   Answering a parked ask.

   The ask is a durable row, not a transient prompt — which is what lets a
   reviewer close the tab and come back to it. So this dialog is a form over a
   record, and closing it loses nothing.

   A DENIAL IS NOT A KILL. A permission-shaped ask can be refused, and the
   refusal is delivered WITH the explanatory answer text: the agent then finishes
   coherently, where an interrupt leaves a half-applied tree in the workspace.
   That is why "Deny" here still requires a sentence.

   The expiry is shown rather than hidden, because if nobody answers before it
   the platform denies on the reviewer's behalf — and a reviewer who did not know
   that will read the outcome as the agent ignoring them.
--------------------------------------------------------------------------- */

export function AnswerAskDialog({
  chatId,
  ask,
  onClose,
}: {
  chatId: number;
  ask: AskBlock | null;
  onClose: () => void;
}) {
  const [answer, setAnswer] = React.useState('');
  const [selected, setSelected] = React.useState<string | null>(null);
  const answerMutation = useAnswerAsk(chatId);
  const now = useNow(Boolean(ask?.expires_at), 1000);

  React.useEffect(() => {
    setAnswer('');
    setSelected(null);
  }, [ask?.ask_id]);

  if (!ask) return null;

  const options = ask.options ?? [];
  const value = selected ?? answer.trim();
  const canSend = value.length > 0 && !answerMutation.isPending;

  const submit = (decision?: 'allow' | 'deny') => {
    if (!canSend) return;
    answerMutation.mutate(
      {
        askId: ask.ask_id,
        body: { answer: value, ...(decision ? { decision } : {}) },
      },
      {
        onSuccess: () => {
          toast('ok', 'Answer delivered', 'The run continues from where it parked.');
          onClose();
        },
        onError: (error) => {
          toast(
            'error',
            'The answer was not accepted',
            error instanceof ApiError
              ? errorMessage(error.code, error.message)
              : 'The answer could not be delivered.',
          );
        },
      },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (answerMutation.isPending && !next) return;
        if (!next) onClose();
      }}
    >
      <DialogContent width="md">
        <DialogHeader
          title="Answer the agent"
          description="The run is parked on this question. Your answer is delivered to the running process; it does not restart anything."
        />

        <DialogBody>
          <div className="border-l-2 border-warn bg-warn-soft px-2.5 py-2">
            <p className="text-sm leading-[19px] text-ink">{ask.question}</p>
            {ask.expires_at ? (
              <p className="mt-1 font-mono text-tiny text-ink-2">
                {formatRemaining(ask.expires_at, now)} — after that the platform denies with an explanation the agent can
                act on, then interrupts after a grace period.
              </p>
            ) : null}
          </div>

          {options.length > 0 ? (
            <div>
              <p className="eyebrow mb-1.5">Choose one</p>
              <div className="flex flex-wrap gap-1.5">
                {options.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setSelected(option.value);
                      setAnswer('');
                    }}
                    className={cn(
                      'rounded-sm border px-2 py-1 font-mono text-xs transition-colors',
                      selected === option.value
                        ? 'border-action bg-action-soft text-ink'
                        : 'border-rule-strong bg-raised text-ink-2 hover:border-ink-3',
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <Field
            label={options.length > 0 ? 'Or answer in your own words' : 'Answer'}
            htmlFor="ask-answer"
          >
            <Textarea
              id="ask-answer"
              rows={3}
              value={answer}
              onChange={(event) => {
                setAnswer(event.target.value);
                setSelected(null);
              }}
              placeholder="What the agent should do"
            />
          </Field>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" disabled={answerMutation.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger-quiet"
            disabled={!canSend}
            loading={answerMutation.isPending}
            onClick={() => submit('deny')}
            title="Refuse, with your explanation attached. The agent finishes coherently rather than being killed."
          >
            Deny with this reason
          </Button>
          <Button variant="primary" disabled={!canSend} loading={answerMutation.isPending} onClick={() => submit()}>
            Send answer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
